/* ═══════════════════════════════════════════════════════════════
   "Save as module…" — the Spec Studio end of the module library
   (docs/MASTERS_UNIFIED_PLAN.md Phase 4, and Phase 6's update half).

   ⛔ A MODULE is a reusable block-group, single-spec only — never a variant.

   The gutter block selection (Phase 1's selection layer) is read ONCE, when
   the dialog opens: what you selected is what gets captured, and nothing the
   dialog does can touch the spec underneath it (`buildModuleDefinition` is
   pure — it copies blocks, it never writes them back).

   Two things the same selection can become:

     · a NEW module            (modules.create)
     · a NEW VERSION of one    (modules.edit) — the definition is replaced by
       what is selected here, version + 1, and every non-project copy of that
       module gets an update to review. ⛔ HARD WALL: project specs are never
       PR'd; they are hand-managed copies.

   The summary is the real definition, not a guess: the same chunking and the
   same anchors that are about to be saved.

   ⛔ R2 PHASE 4: this dialog no longer WRITES. It names the module (or picks
   the one to master), captures the chunks, and hands them to the MODULE
   BUILDER, where the parts get assigned and the structure law is satisfied
   before anything reaches the library (Ruling 6: a module is never created
   in a state the builder would flag). Every capability it had is still here —
   only the final write moved one step later.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { rem } from '../lib/rem';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SearchableSelect } from '../components/SearchableSelect';
import { ModuleCategorySelect } from '../components/ModuleCategorySelect';
import { ModuleTagsInput } from '../components/ModuleTagsInput';
import { CategoriesService } from '../services/categoriesService';
import { collectTags, type ModuleCategory } from '../../shared/module-categories';
import type { ContentBlock } from '../../shared/types';
import { computeNumbering } from '../../shared/body-layout';
import {
  anchorPathLabel,
  buildModuleChunks,
  moduleBlockCount,
  type SpecModule,
} from '../../shared/spec-modules';
import { ModuleService } from '../services/moduleService';
import { reportError } from '../services/errorLog';
import type { ModuleBuilderDraft } from './ModuleBuilder';

type Mode = 'new' | 'update';

export interface SaveAsModuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The blocks of the open spec, in document order. */
  blocks: ContentBlock[];
  /** The gutter selection, captured when the dialog opened. */
  selectedIds: string[];
  sourceSpecId: string;
  sourceCsi?: string;
  /** Who is capturing. Carried for the builder's own bookkeeping — the write
   *  happens there now, so this dialog never stamps a row itself. */
  userName: string;
  /** `modules.create` — may this selection become a NEW module? */
  canCreate?: boolean;
  /** `modules.edit` — may it replace an existing definition? */
  canEditModules?: boolean;
  toast: (msg: string, isError?: boolean) => void;
  /** Hand off to the module builder — where parts are assigned, the structure
   *  law is checked, and the module is actually written. */
  onContinue: (draft: ModuleBuilderDraft) => void;
}

const panelStyle: React.CSSProperties = {
  border: '1px solid var(--bd)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--sa)',
  padding: 'var(--space-3)',
  display: 'grid',
  gap: 'var(--space-2)',
  maxHeight: rem(220),
  overflowY: 'auto',
};

const headingStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: 'var(--tm)',
};

export function SaveAsModuleDialog({
  open,
  onOpenChange,
  blocks,
  selectedIds,
  sourceSpecId,
  sourceCsi,
  userName,
  canCreate = true,
  canEditModules = false,
  toast,
  onContinue,
}: SaveAsModuleDialogProps) {
  const [mode, setMode] = React.useState<Mode>('new');
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [categoryId, setCategoryId] = React.useState<string | null>(null);
  const [tags, setTags] = React.useState<string[]>([]);
  const [metaError, setMetaError] = React.useState<string | null>(null);
  const [categories, setCategories] = React.useState<ModuleCategory[]>([]);
  const [library, setLibrary] = React.useState<SpecModule[]>([]);
  const [loadingLibrary, setLoadingLibrary] = React.useState(false);
  const [targetId, setTargetId] = React.useState('');

  // A fresh dialog every time: last save's name must never ride along.
  React.useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setCategoryId(null);
    setTags([]);
    setMetaError(null);
    setTargetId('');
    setMode(canCreate ? 'new' : 'update');
  }, [open, canCreate]);

  // The category tree (for the picker) and every tag in use (for its
  // suggestions) — needed regardless of mode, so fetched independently of
  // the "update target" library list below.
  const [tagPool, setTagPool] = React.useState<SpecModule[]>([]);
  React.useEffect(() => {
    if (!open) return;
    void CategoriesService.listAll().then(setCategories);
    void ModuleService.list().then(setTagPool);
  }, [open]);
  const tagSuggestions = React.useMemo(() => collectTags(tagPool), [tagPool]);

  // The library is only needed for the update half — fetched when it is.
  React.useEffect(() => {
    if (!open || mode !== 'update' || !canEditModules) return;
    setLoadingLibrary(true);
    ModuleService.list()
      .then(setLibrary)
      .catch((err) => {
        reportError('SpecStudio:moduleLibrary', err);
        toast('The module library could not load', true);
      })
      .finally(() => setLoadingLibrary(false));
    // `toast` is a stable host callback; re-running on it would refetch on
    // every keystroke in the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, canEditModules]);

  // The definition-in-waiting. Ids are minted for real at save; this preview
  // exists only to show the shape, so its ids are thrown away.
  const preview = React.useMemo(() => {
    if (!open) return [];
    const refs = computeNumbering(blocks, null);
    let n = 0;
    return buildModuleChunks(selectedIds, blocks, refs, () => `preview-${++n}`);
  }, [open, blocks, selectedIds]);

  const blockCount = moduleBlockCount({ chunks: preview });
  const target = library.find((m) => m.id === targetId) || null;
  const trimmed = name.trim();
  const canSave =
    preview.length > 0 &&
    (mode === 'new' ? trimmed.length > 0 && canCreate : !!target && canEditModules);

  /** Into the builder. The REAL chunks (fresh ids), not the preview's — the
   *  selection is captured HERE, so what the builder opens on is exactly what
   *  was picked, and nothing further reads the spec. */
  const doContinue = () => {
    if (!canSave) return;
    try {
      const chunks = buildModuleChunks(selectedIds, blocks, computeNumbering(blocks, null), () =>
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `mb-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
      );
      onOpenChange(false);
      onContinue(
        mode === 'new'
          ? {
              module: null,
              seedChunks: chunks,
              name: trimmed,
              description,
              categoryId,
              tags,
              sourceSpecId,
              sourceCsi,
            }
          : {
              module: target,
              seedChunks: chunks,
              name: target?.name,
              description: target?.description,
              sourceSpecId,
              sourceCsi,
            }
      );
    } catch (err) {
      reportError('SpecStudio:saveModule', err);
      toast(
        err instanceof Error && err.message ? err.message : 'That selection could not be captured',
        true
      );
    }
  };

  const oldBlockCount = target ? moduleBlockCount(target) : 0;
  const oldChunkCount = target ? (target.chunks || []).length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'new' ? 'Save as module' : 'Update a module'}</DialogTitle>
          <DialogDescription>
            {blockCount} block{blockCount === 1 ? '' : 's'} in {preview.length} chunk
            {preview.length === 1 ? '' : 's'}
            {sourceCsi ? ` from ${sourceCsi}` : ''}.{' '}
            {mode === 'new'
              ? 'Next you assign each article its part and check the structure. The spec you are editing is not changed — a module is a copy.'
              : 'Next you assign each article its part and check the structure. Saving replaces the module’s blocks: everywhere it has been used gets an update to review — project specs are never changed.'}
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {canCreate && canEditModules && (
            <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
              <TabsList>
                <TabsTrigger value="new">Save as new module</TabsTrigger>
                <TabsTrigger value="update">Update existing module…</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {mode === 'new' ? (
            <>
              <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
                <Label htmlFor="sf-module-name">Name</Label>
                <Input
                  id="sf-module-name"
                  value={name}
                  autoFocus
                  placeholder="e.g. Gypsum board basis of design"
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSave) doContinue();
                  }}
                />
              </div>

              <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
                <Label htmlFor="sf-module-desc">Description (optional)</Label>
                <Input
                  id="sf-module-desc"
                  value={description}
                  placeholder="When should somebody reach for this?"
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
                <Label htmlFor="sf-module-category">Category (optional)</Label>
                <ModuleCategorySelect
                  value={categoryId}
                  onChange={setCategoryId}
                  categories={categories}
                  canCreate={canCreate}
                  userName={userName}
                  ariaLabel="Category"
                  describedBy={metaError ? 'sf-module-meta-error' : undefined}
                  onError={setMetaError}
                />
              </div>

              <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
                <Label htmlFor="sf-module-tags">Tags (optional)</Label>
                <ModuleTagsInput
                  id="sf-module-tags"
                  value={tags}
                  onChange={setTags}
                  suggestions={tagSuggestions}
                  ariaLabel="Tags"
                />
              </div>
              {metaError && (
                <p
                  id="sf-module-meta-error"
                  role="alert"
                  aria-live="assertive"
                  style={{
                    color: 'var(--color-danger)',
                    fontSize: 'var(--font-size-sm)',
                    margin: 0,
                  }}
                >
                  {metaError}
                </p>
              )}
            </>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <Label htmlFor="sf-module-target">Module to update</Label>
              <SearchableSelect
                value={targetId}
                onChange={setTargetId}
                options={library.map((m) => ({
                  value: m.id,
                  label: `${m.name} (v${m.version ?? 1})`,
                }))}
                placeholder={loadingLibrary ? 'Loading the library…' : 'Pick a module'}
                searchPlaceholder="Search modules"
                noResultsText="No modules in the library yet"
                ariaLabel="Module to update"
                disabled={loadingLibrary}
              />
              {target && (
                <p
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--tm)',
                    margin: 'var(--space-1) 0 0',
                  }}
                >
                  v{target.version ?? 1}: {oldBlockCount} block{oldBlockCount === 1 ? '' : 's'} in{' '}
                  {oldChunkCount} chunk{oldChunkCount === 1 ? '' : 's'} → v
                  {(target.version ?? 1) + 1}: {blockCount} block{blockCount === 1 ? '' : 's'} in{' '}
                  {preview.length} chunk{preview.length === 1 ? '' : 's'}
                </p>
              )}
            </div>
          )}

          <div style={panelStyle}>
            <div style={headingStyle}>
              {mode === 'new' ? 'What gets captured' : 'What the new version will be'}
            </div>
            {preview.map((chunk, i) => (
              <div key={i} style={{ display: 'grid', gap: rem(2) }}>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tx)' }}>
                  Chunk {i + 1} — {chunk.blocks.length} block
                  {chunk.blocks.length === 1 ? '' : 's'}
                </div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                  {anchorPathLabel(chunk.anchor)}
                </div>
              </div>
            ))}
            {preview.length === 0 && (
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                Nothing is selected. Close this, click the gutter grips beside the blocks you want,
                then try again.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={doContinue} disabled={!canSave}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
